import React, { memo } from 'react';

const QuizStep = ({ 
  categoryData, 
  answers, 
  onAnswerChange, 
  currentStep,
  totalSteps 
}) => {
  if (!categoryData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading questions...</p>
      </div>
    );
  }

  const { title, subtitle, questions } = categoryData;

  return (
    <div className="animate-fade-in-up">
      {/* Section Header */}
      <div className="text-center mb-8">
        <h2 className="font-display text-3xl font-bold text-primary-dark mb-3">
          {title}
        </h2>
        <p className="text-gray-600 text-lg mx-auto">
          {subtitle}
        </p>
        <div className="mt-4 text-sm text-gray-500">
          Section {currentStep + 1} of {totalSteps}
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-8">
        {questions.map((question, questionIndex) => (
          <QuestionCard
            key={question.id}
            question={question}
            answer={answers[question.id]}
            onAnswerChange={onAnswerChange}
            questionNumber={questionIndex + 1}
          />
        ))}
      </div>
    </div>
  );
};

const QuestionCard = memo(({ question, answer, onAnswerChange, questionNumber }) => {
  const { id, question: questionText, type, options } = question;

  const handleOptionSelect = (option) => {
    onAnswerChange(id, option);
  };

  return (
    <div className="card-wellness p-6 bg-white">
      {/* Question Header */}
      <div className="mb-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-sm">
            {questionNumber}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-800 leading-relaxed">
              {questionText}
            </h3>
          </div>
        </div>
      </div>

      {/* Answer Options */}
      <div className="space-y-3">
        {options.map((option, optionIndex) => {
          const isSelected = answer && answer.text === option.text;
          
          const optionClass = [
            'w-full p-4 text-left border-2 rounded-lg transition-all duration-200 cursor-pointer',
            'hover:border-primary-light hover:bg-blue-50',
            isSelected 
              ? 'border-primary bg-blue-50 text-primary-dark shadow-md' 
              : 'border-gray-200 bg-white text-gray-700'
          ].join(' ');

          return (
            <button
              key={optionIndex}
              type="button"
              className={optionClass}
              onClick={() => handleOptionSelect(option)}
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className={[
                    'w-5 h-5 rounded-full border-2 transition-all duration-200',
                    isSelected 
                      ? 'border-primary bg-primary' 
                      : 'border-gray-300 bg-white'
                  ].join(' ')}>
                    {isSelected && (
                      <div className="w-full h-full rounded-full bg-primary flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 text-sm leading-relaxed">
                  {option.text}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Answer Feedback */}
      {answer && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg animate-fade-in">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-green-700 font-medium">
              ✓ Answer saved
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

export default QuizStep;
